<template>
  <section id="section-support" class="notice-section support-section">
    <header class="notice-section__top support-section__header">
      <div class="notice-section__heading">
        <h2>{{ $t('support.title') }}</h2>
        <p class="support-section__desc">{{ $t('support.desc') }}</p>
      </div>
    </header>

    <article class="support-compose">
      <h3 class="support-compose__title">{{ $t('support.composeTitle') }}</h3>
      <form class="support-compose__form" @submit.prevent="submitQuestion">
        <label class="support-compose__field">
          <span class="support-compose__label">{{ $t('support.fieldTitle') }}</span>
          <input
            v-model.trim="form.title"
            type="text"
            maxlength="100"
            :placeholder="$t('support.fieldTitlePlaceholder')"
            :disabled="submitting"
          >
        </label>
        <label class="support-compose__field">
          <span class="support-compose__label">{{ $t('support.fieldContent') }}</span>
          <textarea
            v-model.trim="form.question"
            rows="6"
            maxlength="2000"
            :placeholder="$t('support.fieldContentPlaceholder')"
            :disabled="submitting"
          />
        </label>
        <p v-if="formError" class="support-compose__error" role="alert">{{ formError }}</p>
        <div class="support-compose__actions">
          <button type="submit" class="support-compose__submit" :disabled="submitting">
            {{ submitting ? $t('support.submitting') : $t('support.submit') }}
          </button>
        </div>
      </form>
    </article>

    <div class="notice-panel support-section__history">
      <div class="support-section__history-head">
        <h3 class="support-section__history-title">{{ $t('support.historyTitle') }}</h3>
        <button
          type="button"
          class="wallet-panel__title-action"
          :disabled="loading"
          :aria-label="$t('support.historyReloadAria')"
          @click="reload"
        >
          <svg
            class="wallet-panel__title-action-icon"
            :class="{ 'slip-section__reload-icon--spin': loading }"
            viewBox="0 0 24 24"
            width="16"
            height="16"
            aria-hidden="true"
          >
            <path
              d="M20 12a8 8 0 1 1-2.34-5.66M20 4v5h-5M4 12a8 8 0 1 1 2.34 5.66M4 20v-5h5"
              fill="none"
              stroke="currentColor"
              stroke-width="1.75"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>
      </div>
      <p v-if="loading" class="notice-section__status">{{ $t('support.loading') }}</p>
      <p v-else-if="loadError" class="notice-section__status notice-section__status--error">{{ loadError }}</p>
      <p v-else-if="!questions.length" class="notice-section__status">{{ $t('support.empty') }}</p>

      <div v-else class="notice-table-wrap">
        <table class="notice-table support-table">
          <thead>
            <tr>
              <th scope="col" class="support-table__col-no">{{ $t('support.colNo') }}</th>
              <th scope="col" class="support-table__col-status">{{ $t('support.colStatus') }}</th>
              <th scope="col" class="support-table__col-title">{{ $t('support.colTitle') }}</th>
              <th scope="col" class="support-table__col-date">{{ $t('support.colDate') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(item, index) in questions"
              :key="item.idx"
              class="notice-table__row"
            >
              <td class="support-table__col-no">{{ questions.length - index }}</td>
              <td class="support-table__col-status">
                <span
                  class="support-table__status"
                  :class="{ 'support-table__status--done': isAnswered(item) }"
                >{{ statusLabel(item) }}</span>
              </td>
              <td class="support-table__col-title">
                <button
                  type="button"
                  class="notice-table__title"
                  @click="openDetail(item.idx)"
                >
                  {{ item.title }}
                </button>
              </td>
              <td class="support-table__col-date">{{ formatDateTime(item.regdate) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </section>
</template>

<script>
import { SystemService } from '@/services'
import { formatDateTime } from '@/utils/format'
import { getQuestionStatusLabel, isQuestionAnswered } from '@/utils/question'
import { showToast } from '@/utils/toast'

export default {
  name: 'BlackGoldSupportPage',
  data () {
    return {
      loading: false,
      loadError: '',
      submitting: false,
      formError: '',
      questions: [],
      form: {
        title: '',
        question: ''
      }
    }
  },
  mounted () {
    this.loadQuestions()
  },
  methods: {
    formatDateTime,
    reload () {
      this.loadQuestions()
    },
    statusLabel (item) {
      return getQuestionStatusLabel(item)
    },
    isAnswered (item) {
      return isQuestionAnswered(item)
    },
    openDetail (idx) {
      this.$emit('open-detail', idx)
    },
    async loadQuestions () {
      this.loading = true
      this.loadError = ''
      try {
        const res = await SystemService.getQuestionList(0, 100)
        if (res.data && res.data.code === 0 && res.data.data) {
          this.questions = Array.isArray(res.data.data.list) ? res.data.data.list : []
        } else {
          this.questions = []
          this.loadError = this.$t('support.loadError')
        }
      } catch (err) {
        this.questions = []
        this.loadError = this.$t('support.loadError')
      } finally {
        this.loading = false
      }
    },
    async submitQuestion () {
      if (this.submitting) return
      this.formError = ''
      if (!this.form.title) {
        this.formError = this.$t('support.errorTitleRequired')
        return
      }
      if (!this.form.question) {
        this.formError = this.$t('support.errorContentRequired')
        return
      }

      this.submitting = true
      try {
        const res = await SystemService.createQuestion(this.form.title, this.form.question)
        if (res.data && res.data.code === 0) {
          this.form.title = ''
          this.form.question = ''
          showToast(this.$t('support.submitSuccess'))
          await this.loadQuestions()
        } else {
          this.formError = this.$t('support.submitFail')
        }
      } catch (err) {
        this.formError = this.$t('support.submitFail')
      } finally {
        this.submitting = false
      }
    }
  }
}
</script>
